import { useEffect, useState } from "react";
import { Field, Input, Label } from "@headlessui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { handleEnterSpaceKey } from "../../../../app/keyboardUtils";
import { useAppSelector } from "../../../../app/hooks";
import RequiredField from "../../../../components/RequiredField";
import SearchableSelect from "../../../../components/input/SearchableSelect";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import {
	_defaultCountry,
	useGetCountriesQuery,
	useLazyGetZonesQuery,
} from "../../../countries/countriesApiSlice";

type DistrictAddressInfo = {
	address: string;
	address2: string;
	city: string;
	zoneId?: number;
	countryId?: number;
	zip: string;
	contactName: string;
	emailAddress: string;
	phone: string;
};

export interface DistrictAddressInfoSectionProps {
	value: DistrictAddressInfo;
	onAddressChange: (patch: Partial<DistrictAddressInfo>) => void;
	editMode?: boolean;
	showAddressSection: boolean;
	toggleShowAddressSection: () => void;
}

export default function DistrictAddressInfoSection({
	value,
	onAddressChange,
	showAddressSection,
	toggleShowAddressSection,
	editMode,
}: DistrictAddressInfoSectionProps) {
	const { config } = useAppSelector((state) => state.site);

	// The section is only used for adding when it isn't in edit mode.
	const addingNew = !editMode;

	const [didSeedDefaults, setDidSeedDefaults] = useState(false);
	const [stateOptions, setStateOptions] = useState<SimpleOption[]>([]);
	const [countryOptions, setCountryOptions] = useState<SimpleOption[]>([]);

	const getCountriesResult = useGetCountriesQuery();
	const countries = getCountriesResult.data;

	const [callGetZonesForCountry] = useLazyGetZonesQuery();

	useEffect(() => {
		if (!countries) return;

		setCountryOptions(countries.map((c) => ({ value: c.id, label: c.name }) as SimpleOption));

		// The country defaults to the United States.  This is display only: nothing stores a
		// country on its own, so defaulting it can't give the district an address it never had.
		// The state is the part that persists, and that one stays blank until someone picks it.
		if (!value.countryId) {
			onAddressChange({
				countryId: countries.find((c) => c.name == _defaultCountry)?.id,
			});
		}
	}, [countries]);

	useEffect(() => {
		if (!value.countryId) {
			setStateOptions([]);
			return;
		}

		let cancelled = false;

		callGetZonesForCountry(value.countryId)
			.unwrap()
			.then((zones) => {
				if (cancelled) return;

				const options = (zones ?? []).map(
					(z) => ({ value: z.id, label: z.name, code: z.code }) as SimpleOption
				);
				setStateOptions(options);

				// Seed the state once, for a new district, and only when the site actually
				// configures a default.  Without one the state stays blank for the user to
				// pick, rather than landing on whichever state happens to sort first.
				if (addingNew && !didSeedDefaults && !value.zoneId && config?.defaultState) {
					const zoneId = options.find((z) => z.label == config.defaultState)?.value;

					if (zoneId != null) onAddressChange({ zoneId: Number(zoneId) });
				}

				setDidSeedDefaults(true);
			});

		return () => {
			cancelled = true;
		};
	}, [value.countryId]);

	// The selected state belongs to the old country, so clear it instead of carrying it over.
	function handleCountryChange(countryId: number) {
		onAddressChange({ countryId: countryId, zoneId: undefined });
	}

	return (
		<div className="mt-2">
			<div className="flex items-center cursor-pointer" onClick={toggleShowAddressSection}>
				{showAddressSection ? (
					<ChevronDownIcon
						tabIndex={0}
						className="size-5 hover:cursor-pointer"
						onClick={(e) => {
							e.stopPropagation();
							toggleShowAddressSection();
						}}
						onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
					/>
				) : (
					<ChevronRightIcon
						tabIndex={0}
						className="size-5 hover:cursor-pointer"
						onClick={(e) => {
							e.stopPropagation();
							toggleShowAddressSection();
						}}
						onKeyDown={(e) => handleEnterSpaceKey(e, toggleShowAddressSection)}
					/>
				)}
				<h3 className="text-base font-semibold text-gray-900 pt-2 pb-2 ml-4">
					Address and Contact Information
				</h3>
			</div>
			{showAddressSection && (
				<div className="flex gap-x-12 w-full">
					<div className="flex-col w-3/5">
						<h2 className="mb-2">Address</h2>
						<div
							className={`${editMode ? "gap-y-4" : "gap-y-2"} grid grid-cols-2 md:grid-cols-9 gap-x-6`}>
							<Field
								className={"col-span-2 md:col-span-full"}>
								<Label htmlFor="line1" className="font-medium text-sm">
									Street Address
								</Label>
								<Input
									id="line1"
									name="line1"
									type="text"
									maxLength={255}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.address ?? ""}
									onChange={(e) => {
										onAddressChange({ address: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={"col-span-2 md:col-span-full"}>
								<Label htmlFor="line2" className="font-medium text-sm">
									Street Address Line 2
								</Label>
								<Input
									id="line2"
									name="line2"
									type="text"
									maxLength={150}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.address2 ?? ""}
									onChange={(e) => {
										onAddressChange({ address2: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={"col-span-2 md:col-span-4"}>
								<Label htmlFor="city" className="font-medium text-sm">
									City
									<RequiredField />
								</Label>
								<Input
									id="city"
									name="city"
									type="text"
									required
									maxLength={50}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.city ?? ""}
									onChange={(e) => {
										onAddressChange({ city: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={"col-span-2 md:col-span-5"}>
								<SearchableSelect
									value={value.zoneId}
									setValue={(v) => onAddressChange({ zoneId: Number(v) })}
									label="State"
									requiredField={true}
									prompt="Select a state"
									id="stateDropdown"
									options={stateOptions}
								/>
							</Field>
							<Field
								className={"col-span-2 md:col-span-6"}>
								<SearchableSelect
									value={value.countryId}
									setValue={(v) => handleCountryChange(Number(v))}
									label="Country"
									requiredField={true}
									prompt="Select a country"
									id="countryDropdown"
									options={countryOptions}
								/>
							</Field>
							<Field
								className={"col-span-1 md:col-span-3"}>
								<Label htmlFor="zip" className="font-medium text-sm">
									ZIP Code
								</Label>
								<Input
									id="zip"
									name="zip"
									type="text"
									maxLength={10}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.zip ?? ""}
									onChange={(e) => {
										onAddressChange({ zip: e.target.value });
									}}
								/>
							</Field>
						</div>
					</div>
					<div className="flex-col w-2/5">
						<h2 className="mb-2">Contact Information</h2>
						<div
							className={`${editMode ? "gap-y-4 sm:grid-cols-6" : "gap-y-2"} grid grid-cols-1`}>
							<Field className={`${editMode ? "col-span-5" : "col-span-full"}`}>
								<Label htmlFor="contactName" className="font-medium text-sm">
									Name
									<RequiredField />
								</Label>
								<Input
									id="contactName"
									name="contactName"
									type="text"
									required
									maxLength={60}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.contactName ?? ""}
									onChange={(e) => {
										onAddressChange({ contactName: e.target.value });
									}}
								/>
							</Field>
							<Field className={`${editMode ? "col-span-5" : "col-span-full"}`}>
								<Label htmlFor="email" className="font-medium text-sm">
									Email
								</Label>
								<Input
									id="email"
									name="email"
									type="text"
									maxLength={255}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.emailAddress ?? ""}
									onChange={(e) => {
										onAddressChange({ emailAddress: e.target.value });
									}}
								/>
							</Field>
							<Field
								className={`${editMode ? "col-span-4 lg:col-span-3" : "col-span-full"}`}>
								<Label htmlFor="phone" className="font-medium text-sm">
									Phone
								</Label>
								<Input
									id="phone"
									name="phone"
									type="text"
									maxLength={14}
									className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
									value={value.phone ?? ""}
									onChange={(e) => {
										onAddressChange({ phone: e.target.value });
									}}
								/>
							</Field>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
